import { useCallback, useMemo, useState } from 'react';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { useCloseJSA, useJSAs } from '@/hooks/use-safety';
import { useOrgToday } from '@/hooks/use-settings';
import type { JSA } from '@/lib/types';

const DISMISSED_KEY_PREFIX = 'previous-day-jsa-dismissed:';

function readDismissed(orgToday: string): number[] {
  try {
    const raw = window.localStorage.getItem(`${DISMISSED_KEY_PREFIX}${orgToday}`);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
}

function writeDismissed(orgToday: string, ids: number[]) {
  try {
    // Only today's key matters; drop keys from earlier days.
    for (let i = window.localStorage.length - 1; i >= 0; i -= 1) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(DISMISSED_KEY_PREFIX) && key !== `${DISMISSED_KEY_PREFIX}${orgToday}`) {
        window.localStorage.removeItem(key);
      }
    }
    window.localStorage.setItem(`${DISMISSED_KEY_PREFIX}${orgToday}`, JSON.stringify(ids));
  } catch {
    // Storage unavailable (private mode etc.): the dismissal still holds for this page view.
  }
}

/**
 * Finds open JSAs from a previous day on a project and offers to close them,
 * one at a time. Only users allowed to close JSAs (safety manager+, same as
 * the close endpoint) are prompted. "Not now" hides the prompt for that JSA
 * for the rest of the org's day.
 */
export function usePreviousDayJSAWarning(projectId: number | null, enabled = true) {
  const orgToday = useOrgToday();
  const { hasMinModuleRole } = usePermissions();
  const canCloseJSAs = hasMinModuleRole('safety', 'manager');
  const closeMutation = useCloseJSA();
  // JSAs closed or dismissed on this page view (dismissals are also stored
  // for the rest of the day; this covers storage being unavailable).
  const [handledThisView, setHandledThisView] = useState<number[]>([]);
  const { data } = useJSAs(
    {
      project: projectId ?? undefined,
      page_size: 50,
      ordering: '-date',
    },
    enabled && canCloseJSAs && Boolean(projectId),
  );

  const prompt: JSA | null = useMemo(() => {
    if (!projectId || !orgToday || !canCloseJSAs || !enabled) return null;
    const dismissed = new Set([...readDismissed(orgToday), ...handledThisView]);
    return (
      data?.results.find(
        (jsa) =>
          jsa.date < orgToday &&
          (jsa.status === 'active' || jsa.status === 'pending_signatures') &&
          !dismissed.has(jsa.id),
      ) ?? null
    );
  }, [canCloseJSAs, data?.results, enabled, handledThisView, orgToday, projectId]);

  const dismiss = useCallback(() => {
    if (!prompt || !orgToday) return;
    writeDismissed(orgToday, [...readDismissed(orgToday), prompt.id]);
    setHandledThisView((ids) => [...ids, prompt.id]);
  }, [orgToday, prompt]);

  const close = useCallback(() => {
    if (!prompt) return;
    const jsaId = prompt.id;
    setHandledThisView((ids) => [...ids, jsaId]);
    closeMutation.mutate(jsaId, {
      onError: () => toast.error("The previous day's JSA could not be closed."),
    });
  }, [closeMutation, prompt]);

  return { prompt, close, dismiss };
}
